/**
 * LatencyDistributionChart - Horizontal bar chart showing latency distribution
 *
 * Features:
 * - 5 fixed latency buckets (<1s, 1-5s, 5-15s, 15-60s, 60s+)
 * - Y-axis: Latency ranges in seconds
 * - X-axis: Number of traces per bucket
 * - Click to drilldown to Traces page with latency filter
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from 'recharts';
import { palette } from '../../utils/designTokens';
import { serializeFilters } from '../../utils/urlBuilder';

/**
 * Format latency from milliseconds to seconds
 * - Use 1 decimal place if >= 1s
 * - Use 3 decimal places if < 1s
 */
const formatLatencySeconds = (ms) => {
  if (ms === null || ms === undefined) return null;
  const seconds = ms / 1000;
  return seconds >= 1 ? seconds.toFixed(1) : seconds.toFixed(3);
};

const formatBucketLabel = (bucket) => {
  const minSeconds = formatLatencySeconds(bucket.min_latency_ms);
  const maxSeconds = formatLatencySeconds(bucket.max_latency_ms);

  if (bucket.max_latency_ms === null || bucket.max_latency_ms === undefined) {
    return `${minSeconds}s+`;
  }
  if (!bucket.min_latency_ms || bucket.min_latency_ms <= 0) {
    return `< ${maxSeconds}s`;
  }
  return `${minSeconds} - ${maxSeconds}s`;
};

/**
 * Custom Tooltip
 */
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;
  const totalTraces = data.totalTraces || 0;
  const percentage = totalTraces > 0 ? ((data.trace_count / totalTraces) * 100).toFixed(1) : 0;

  return (
    <div
      style={{
        backgroundColor: palette.neutrals.panelQuaternary,
        border: `1px solid ${palette.neutrals.borderSubtle}`,
        borderRadius: '8px',
        padding: '12px',
        boxShadow: palette.quality.shadowCard
      }}
    >
      <div style={{ color: palette.text.primary, fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
        {data.label}
      </div>
      <div style={{ color: palette.text.supporting, fontSize: '12px', marginBottom: '4px' }}>
        Traces: <span style={{ color: palette.text.primary, fontWeight: 600 }}>{data.trace_count.toLocaleString()}</span>
      </div>
      <div style={{ color: palette.text.muted, fontSize: '11px' }}>
        {percentage}% of total
      </div>
    </div>
  );
};

const LatencyDistributionChart = ({ data, appId, environment, timeWindow }) => {
  const navigate = useNavigate();

  // Process data for the chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Calculate total traces for percentage calculation
    const totalTraces = data.reduce((sum, bucket) => sum + bucket.trace_count, 0);

    // Map and reverse to show largest range first (top of chart)
    return data.map(bucket => {
      return {
        bucket_index: bucket.bucket_index,
        label: formatBucketLabel(bucket),
        trace_count: bucket.trace_count,
        min_latency_ms: bucket.min_latency_ms,
        max_latency_ms: bucket.max_latency_ms,
        totalTraces,
      };
    }).reverse();
  }, [data]);

  // Handle bar click for drilldown
  const handleBarClick = (data) => {
    if (!data) return;

    const filters = {
      min_latency: data.min_latency_ms,
      max_latency: data.max_latency_ms,
      env: environment,
      range: timeWindow,
      from: 'performance'
    };
    const url = `/app/${appId}/traces?${serializeFilters(filters)}`;
    navigate(url);
  };

  const hasData = chartData.length > 0;

  if (!hasData) {
    return (
      <div
        style={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: palette.text.muted,
          fontSize: '14px'
        }}
      >
        No latency distribution data available
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '280px' }}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={palette.neutrals.borderSubtle}
            opacity={0.3}
          />
          <XAxis
            type="number"
            tick={{ fill: palette.text.muted, fontSize: 11 }}
            axisLine={{ stroke: palette.neutrals.borderSubtle }}
            tickLine={false}
            label={{ value: 'Number of traces', position: 'insideBottom', offset: -5, fill: palette.text.muted, fontSize: 11 }}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fill: palette.text.muted, fontSize: 11 }}
            axisLine={{ stroke: palette.neutrals.borderSubtle }}
            tickLine={false}
            width={120}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
          <Bar
            dataKey="trace_count"
            cursor="pointer"
            onClick={(data) => handleBarClick(data)}
            minPointSize={5}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={palette.accents.primary.base}
                opacity={0.8}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LatencyDistributionChart;
